import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Upload, X, Minus, Plus, Gift, Users, PartyPopper, Truck, Mail, Package } from "lucide-react";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const OCCASIONS = ["Birthday", "Wedding", "Anniversary", "Graduation", "Engagement", "Holiday / Christmas", "Retirement", "Just because", "Other"];

export default function OrderPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [pkg, setPkg] = useState("single_person");
  const [extra, setExtra] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "", occasion: "", occasion_details: "",
    notes: "", referred_by: "", promo_opt_in: false, promo_email: "",
    delivery_method: "email", shipping_address: "",
  });

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data))
      .catch((error) => console.error("Failed to load products:", error));
  }, []);

  const packageOptions = useMemo(() => products.filter((p) => p.lookup_key !== "extra_person"), [products]);
  const base = products.find((p) => p.lookup_key === pkg);
  const extraProduct = products.find((p) => p.lookup_key === "extra_person");
  const basePeople = { single_person: 1, couple: 2, family_group: 4 }[pkg] || 1;
  const maxExtra = Math.max(0, 6 - basePeople);
  const subtotal = (base?.price || 0) + extra * (extraProduct?.price || 0);
  const eligibleDiscount = !!(form.referred_by && form.referred_by.trim());

  const onDrop = useCallback(async (files) => {
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { id: res.data.id, name: file.name }]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setUploading(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { "image/*": [] } });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.email.trim()) { toast.error("Please add your name and email"); return; }
    if (photos.length === 0) { toast.error("Please upload at least one reference photo"); return; }
    if ((form.delivery_method === "mail" || form.delivery_method === "both") && !form.shipping_address.trim()) {
      toast.error("Please add a shipping address for mailed delivery"); return;
    }
    setSubmitting(true);
    const items = [{ lookup_key: base.lookup_key, name: base.name, price: base.price, quantity: 1 }];
    if (extra > 0) items.push({ lookup_key: "extra_person", name: "Additional Person", price: extraProduct.price, quantity: extra });
    try {
      const res = await api.post("/orders", {
        ...form, items, photo_ids: photos.map((p) => p.id),
      });
      toast.success("Order created! On to payment 🎉");
      navigate(`/payment/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create order");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-12" data-testid="order-page">
      <span className="font-accent text-3xl text-bubble">let's make art</span>
      <h1 className="font-display font-bold text-4xl sm:text-5xl">Order your caricature</h1>
      <p className="text-ink/60 text-lg mt-1 mb-10">Fill this out and we'll get sketching.</p>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* PACKAGE */}
          <section className="brut-card p-6">
            <h2 className="font-display font-bold text-2xl mb-4 flex items-center gap-2"><Users className="w-6 h-6 text-bubble" /> Choose your package</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {packageOptions.map((p) => (
                <button type="button" key={p.lookup_key} onClick={() => { setPkg(p.lookup_key); const bp = { single_person: 1, couple: 2, family_group: 4 }[p.lookup_key] || 1; setExtra((e) => Math.min(e, 6 - bp)); }} data-testid={`pkg-${p.lookup_key}`}
                  className={`text-left brut border-ink p-4 transition-[transform,box-shadow] hover:-translate-y-0.5 ${pkg === p.lookup_key ? "bg-sun shadow-brutlg" : "bg-white shadow-brut"}`}>
                  <div className="font-display font-bold text-lg">{p.name}</div>
                  <div className="text-xs text-ink/60">{p.people}</div>
                  <div className="font-display font-bold text-2xl mt-2">${p.price}</div>
                </button>
              ))}
            </div>
            <div className="mt-5 flex items-center justify-between brut border-ink bg-cream-solid p-4">
              <div>
                <div className="font-display font-bold">Additional people</div>
                <div className="text-xs text-ink/60">+${extraProduct?.price || 10} each (up to 6 total)</div>
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setExtra(Math.max(0, extra - 1))} className="brut-btn !p-2 bg-white" data-testid="extra-minus"><Minus className="w-4 h-4" /></button>
                <span className="font-display font-bold text-xl w-6 text-center" data-testid="extra-count">{extra}</span>
                <button type="button" onClick={() => setExtra(Math.min(maxExtra, extra + 1))} className="brut-btn !p-2 bg-white" data-testid="extra-plus"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          </section>

          {/* PHOTOS */}
          <section className="brut-card p-6">
            <h2 className="font-display font-bold text-2xl mb-4">Send your photos</h2>
            <div {...getRootProps()} data-testid="photo-dropzone"
              className={`border-[3px] border-dashed border-ink rounded-2xl p-8 text-center cursor-pointer transition-colors ${isDragActive ? "bg-sky/40" : "bg-cream-solid hover:bg-sun/30"}`}>
              <input {...getInputProps()} />
              <Upload className="w-10 h-10 mx-auto mb-2" />
              <p className="font-display font-semibold">{isDragActive ? "Drop them here!" : "Drag & drop photos, or click to browse"}</p>
              <p className="text-sm text-ink/50">Clear, well-lit photos work best.</p>
            </div>
            {uploading && <p className="mt-3 font-semibold text-tangerine">Uploading…</p>}
            {photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
                {photos.map((p, i) => (
                  <div key={p.id} className="relative brut border-ink overflow-hidden" data-testid={`photo-${i}`}>
                    <img src={`${API}/files/${p.id}`} alt={p.name} className="w-full h-24 object-cover" />
                    <button type="button" onClick={() => setPhotos(photos.filter((x) => x.id !== p.id))}
                      className="absolute top-1 right-1 bg-white border-2 border-ink rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* DETAILS */}
          <section className="brut-card p-6 space-y-4">
            <h2 className="font-display font-bold text-2xl flex items-center gap-2"><PartyPopper className="w-6 h-6 text-tangerine" /> What's it for?</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-display font-semibold text-sm">Occasion</label>
                <Select value={form.occasion} onValueChange={(v) => setForm({ ...form, occasion: v })}>
                  <SelectTrigger className="brut-input mt-1" data-testid="occasion-select"><SelectValue placeholder="Pick an occasion" /></SelectTrigger>
                  <SelectContent>{OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-display font-semibold text-sm">Details (optional)</label>
                <Input value={form.occasion_details} onChange={(e) => setForm({ ...form, occasion_details: e.target.value })}
                  className="brut-input mt-1" placeholder="e.g. Mom's 60th surprise" data-testid="occasion-details" />
              </div>
            </div>
            <div>
              <label className="font-display font-semibold text-sm">Anything else? (style, background, deadline…)</label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="brut-input mt-1 min-h-24" data-testid="order-notes" />
            </div>
          </section>

          {/* DELIVERY */}
          <section className="brut-card p-6 space-y-4">
            <h2 className="font-display font-bold text-2xl flex items-center gap-2"><Truck className="w-6 h-6 text-sky" /> How should we deliver it?</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { id: "email", label: "By email", icon: Mail, desc: "Digital file to print & frame" },
                { id: "mail", label: "Mailed to you", icon: Package, desc: "Physical copy shipped" },
                { id: "both", label: "Both", icon: Truck, desc: "Email + mailed" },
              ].map((d) => (
                <button type="button" key={d.id} onClick={() => setForm({ ...form, delivery_method: d.id })} data-testid={`delivery-${d.id}`}
                  className={`text-left brut border-ink p-4 transition-[transform,box-shadow] hover:-translate-y-0.5 ${form.delivery_method === d.id ? "bg-sky shadow-brutlg" : "bg-white shadow-brut"}`}>
                  <d.icon className="w-6 h-6 mb-2" />
                  <div className="font-display font-bold">{d.label}</div>
                  <div className="text-xs text-ink/60">{d.desc}</div>
                </button>
              ))}
            </div>
            {(form.delivery_method === "mail" || form.delivery_method === "both") && (
              <div>
                <label className="font-display font-semibold text-sm">Shipping address *</label>
                <Textarea value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                  className="brut-input mt-1 min-h-20" placeholder="Full name, street, city, state, ZIP" data-testid="shipping-address" />
              </div>
            )}
          </section>

          {/* CONTACT */}
          <section className="brut-card p-6 space-y-4">
            <h2 className="font-display font-bold text-2xl">Your contact info</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-display font-semibold text-sm">Full name *</label>
                <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="brut-input mt-1" data-testid="order-name" />
              </div>
              <div>
                <label className="font-display font-semibold text-sm">Email *</label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="brut-input mt-1" data-testid="order-email" />
              </div>
              <div>
                <label className="font-display font-semibold text-sm">Phone</label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="brut-input mt-1" data-testid="order-phone" />
              </div>
              <div>
                <label className="font-display font-semibold text-sm">Referred by (get 10% off!)</label>
                <Input value={form.referred_by} onChange={(e) => setForm({ ...form, referred_by: e.target.value })}
                  className="brut-input mt-1" placeholder="Their name or email" data-testid="order-referral" />
              </div>
            </div>

            <div className="brut border-ink bg-sun/40 p-4 flex items-start gap-3">
              <Mail className="w-6 h-6 mt-1 text-tangerine shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-display font-bold">Join our mailing list</span>
                  <Switch checked={form.promo_opt_in} onCheckedChange={(v) => setForm({ ...form, promo_opt_in: v })} data-testid="promo-toggle" />
                </div>
                <p className="text-sm text-ink/60">Get news, new examples & occasional offers by email.</p>
                {form.promo_opt_in && (
                  <Input type="email" value={form.promo_email} onChange={(e) => setForm({ ...form, promo_email: e.target.value })}
                    className="brut-input mt-3" placeholder="Email for updates" data-testid="promo-email" />
                )}
              </div>
            </div>
          </section>
        </div>

        {/* SUMMARY */}
        <div className="lg:col-span-1">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="brut-card p-6 sticky top-24" data-testid="order-summary">
            <h2 className="font-display font-bold text-2xl mb-4">Summary</h2>
            <div className="flex justify-between py-2 border-b-2 border-dashed border-ink/30">
              <span>{base?.name || "Package"}</span><span className="font-bold">${base?.price?.toFixed(2) || "0.00"}</span>
            </div>
            {extra > 0 && (
              <div className="flex justify-between py-2 border-b-2 border-dashed border-ink/30">
                <span>+{extra} extra {extra === 1 ? "person" : "people"}</span>
                <span className="font-bold">${(extra * (extraProduct?.price || 0)).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between py-3 text-lg">
              <span className="font-display font-bold">Subtotal</span>
              <span className="font-display font-bold" data-testid="order-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            {eligibleDiscount && (
              <div className="tag-pill bg-tangerine/30 mb-3"><Gift className="w-3 h-3" /> 10% off may apply at checkout</div>
            )}
            <button type="submit" disabled={submitting || uploading} className="brut-btn bg-bubble text-white w-full text-lg" data-testid="order-submit">
              {submitting ? "Creating…" : "Continue to payment"}
            </button>
            <p className="text-xs text-ink/50 mt-3 text-center">Referral discounts are verified against past customers.</p>
          </motion.div>
        </div>
      </form>
    </div>
  );
}
